/**
 * @typedef BackInStockAttributes
 *
 * @property {boolean} [accepts_marketing]
 * @property {string} [phone_number] Required if email is null
 * @property {number} [quantity_required]
 */

/**
 * @typedef {Object} BackInStockSuccessResponse
 *
 * @property {'OK'} status
 * @property {string} message
 */

/**
 * @typedef {Object} BackInStockErrorResponse
 *
 * @property {'Error'} status
 * @property {Record<string, string[]>} errors
 */

class BackInStock {
  /**
   * @note It might be worth abstracting this further, for example simplifying the function signature
   * to have object containing email and phone number. That way this API could be easier to swap out
   * for other back ends in the future. It doesn't seem necessary at the moment though. - DW
   *
   * @param {string | null} email
   * @param {number} variantId
   * @param {number} productId
   * @param {BackInStockAttributes} [attributes]
   */
  static async subscribe(email, variantId, productId, attributes) {
    try {
      const response = await window.BIS.create(
        email,
        variantId,
        productId,
        attributes,
      );

      if (response.status === "Error") {
        // Flatten errors into a readable string
        const errs = response?.errors || {};
        const parts = [];

        for (const [field, msgs] of Object.entries(errs)) {
          const list = Array.isArray(msgs) ? msgs : [String(msgs || "")];
          list
            .filter(Boolean)
            .forEach((m) =>
              parts.push(field === "base" ? m : `${field}: ${m}`),
            );
        }

        response.message = parts.length
          ? parts.join(" ")
          : response.message || "Something went wrong. Please try again.";
      }

      return response;
    } catch (err) {
      return {
        status: "Error",
        message: "Could not submit your request. Please try again.",
      };
    }
  }
}

class BackInStockForm extends window.BAO.CustomElement() {
  static get requiredElements() {
    return ["form", "state", "message"];
  }

  setupListeners() {
    super.setupListeners();

    this.listeners.add(
      this.els.form.element,
      "submit",
      this.handleFormSubmit.bind(this),
    );

    // Listen for your theme’s variant change
    this.listeners.add(document, "variant:change", (e) => {
      const id = e?.detail?.variant?.id;
      if (id) this.dataset.variantId = String(id);
    });
  }

  /** @param {SubmitEvent} event */
  handleFormSubmit(event) {
    event.preventDefault();

    this.message = "";

    this.subscribe();
  }

  async subscribe() {
    const fd = new FormData(this.els.form.element);
    const email = (fd.get("email") || "").toString().trim() || null;
    let phoneRaw = (fd.get("phone") || "").toString().trim() || null;
    let phone = phoneRaw ? normalisePhoneToE164(phoneRaw, "GB") : null;

    // Require at least one
    if (!email && !phone) {
      this.state = "idle";
      this.message = "Please enter an email address or mobile number.";
      return;
    }

    // If user provided a phone but it couldn’t be normalised, block early
    if (phoneRaw && !phone) {
      this.state = "idle";
      this.message =
        "Please enter a valid mobile number with country code (e.g. +447... for UK).";
      return;
    }

    // If we normalised, also sanity check it
    if (phone && !looksLikeE164(phone)) {
      this.state = "idle";
      this.message =
        "Your mobile number looks invalid. Use international format like +447...";
      return;
    }

    const attrsBase = { accepts_marketing: !!fd.get("accepts_marketing") };

    // Disable submit while working (optional but nice UX)
    const submitters = this.els.form.element.querySelectorAll(
      '[type="submit"], button[type="submit"]',
    );
    submitters.forEach((b) => (b.disabled = true));

    // Helper to label/normalize messages
    const labelMsg = (label, resp) => {
      const t = (resp?.message || "").trim();
      return t ? `${label}: ${t}` : "";
    };

    let successes = 0;
    const messages = [];

    // Email call
    if (email) {
      const respEmail = await BackInStock.subscribe(
        email,
        this.variantId,
        this.productId,
        { ...attrsBase },
      );
      if (respEmail?.status === "OK") successes++;
      else
        messages.push(
          `Email subscription error: ${respEmail?.message || "Unknown error"}`,
        );
    }

    // SMS call (email must be null)
    if (phone) {
      const respSms = await BackInStock.subscribe(
        null,
        this.variantId,
        this.productId,
        { ...attrsBase, phone_number: phone },
      );
      if (respSms?.status === "OK") successes++;
      else
        messages.push(
          `SMS subscription error: ${respSms?.message || "Unknown error"}`,
        );
    }

    // UI outcome: success if at least one succeeded; otherwise idle + show errors
    if (successes > 0) {
      this.state = "success";
      // If one failed, surface that; otherwise keep success view clean
      const hadFailures = email && phone && successes === 1;
      this.message = hadFailures
        ? messages.filter((m) => /error/i.test(m)).join(" ")
        : "";
    } else {
      this.state = "idle";
      // Show combined error messages (deduped)
      this.message = Array.from(new Set(messages.filter(Boolean))).join(" ");
    }

    submitters.forEach((b) => (b.disabled = false));
  }

  /** @returns {'idle' | 'success'} */
  get state() {
    const visibleState = this.els.state.elements.find(
      (el) => el.getAttribute("aria-hidden") !== "true",
    );
    return visibleState?.dataset.state || "idle";
  }

  set state(state) {
    this.els.state.elements.forEach((el) => {
      el.setAttribute("aria-hidden", el.dataset.state !== state);
    });
  }

  get productId() {
    return Number(this.dataset.productId);
  }

  /** @todo unsure this updates as variant is changed */
  get variantId() {
    return Number(this.dataset.variantId);
  }

  get message() {
    return this._message;
  }

  set message(message) {
    this._message = message;

    this.els.message?.elements?.forEach((messageEl) => {
      messageEl.textContent = message;
      messageEl.setAttribute("aria-hidden", !message?.length);
    });
  }
}

if (!window.customElements.get("back-in-stock-form")) {
  window.customElements.define("back-in-stock-form", BackInStockForm, {
    extends: "aside",
  });
}

function normalisePhoneToE164(input, defaultCountry = "GB") {
  if (!input) return null;
  let raw = String(input).replace(/[^\d+]/g, "");

  // If starts 00, convert to +
  if (raw.startsWith("00")) raw = "+" + raw.slice(2);

  // If starts with + already, assume user typed E.164
  if (raw.startsWith("+")) return raw;

  // Country-specific quick normalisation (GB)
  if (defaultCountry === "GB") {
    // Strip leading 0 and add +44
    if (raw.startsWith("0")) raw = "+44" + raw.slice(1);
    else raw = "+44" + raw; // very rough fallback
    return raw;
  }

  // Fallback: if we have no +, we can't be certain → return null to fail validation
  return null;
}

function looksLikeE164(phone) {
  // very light check: + followed by 8–15 digits
  return typeof phone === "string" && /^\+\d{8,15}$/.test(phone);
}
